<template>
  <div class="component-upload-image">
    <el-upload
      multiple
      :action="uploadImgUrl"
      list-type="picture-card"
      :on-success="handleUploadSuccess"
      :before-upload="handleBeforeUpload"
      :limit="limit"
      :on-error="handleUploadError"
      drag
      :on-exceed="handleExceed"
      ref="imageUpload"
      :before-remove="handleDelete"
      :show-file-list="true"
      :headers="headers"
      :file-list="fileList"
      :on-preview="handlePictureCardPreview"
      :class="{ hide: fileList.length >= limit }"
    >
      <el-icon class="avatar-uploader-icon"><plus /></el-icon>
      
      <!-- 自定义文件列表项 -->
      <template #file="{ file }">
        <div v-if="isVideoFile(file)" class="video-file-item" @mouseenter="loadVideoPoster(file)">
          <!-- 使用首帧图作为预览，鼠标悬停时显示视频 -->
          <!-- <img
            v-if="videoPosters.value[file.url]"
            :src="videoPosters.value[file.url]"
            class="video-poster"
          /> -->

          <video
            :src="file.url"
            controlsList="nodownload"
            preload="metadata"
            class="video-preview"
       
          >
            <source :src="file.url" type="video/mp4" />
          </video>
          <span class="el-upload-list__item-actions">
            <span
              class="el-upload-list__item-preview"
              @click="handlePictureCardPreview(file)"
            >
              <el-icon><zoom-in /></el-icon>
            </span>
            <span
              v-if="!disabled"
              class="el-upload-list__item-delete"
              @click="handleDelete(file)"
            >
              <el-icon><Delete /></el-icon>
            </span>
          </span>
        </div>
        <div v-else>
          <img :src="file.url" class="el-upload-list__item-thumbnail" />
          <span class="el-upload-list__item-actions">
            <span
              class="el-upload-list__item-preview"
              @click="handlePictureCardPreview(file)"
            >
              <el-icon><zoom-in /></el-icon>
            </span>
            <span
              v-if="!disabled"
              class="el-upload-list__item-delete"
              @click="handleDelete(file)"
            >
              <el-icon><Delete /></el-icon>
            </span>
          </span>
        </div>
      </template>
    </el-upload>
    <!-- 上传提示 -->
    <div class="el-upload__tip" v-if="showTip">
      请上传
      <template v-if="fileSize">
        大小不超过 <b style="color: #f56c6c">{{ fileSize }}MB</b>
      </template>
      <template v-if="fileType">
        格式为 <b style="color: #f56c6c">{{ fileType.join("/") }}</b>
      </template>
      的视频
    </div>

     <el-dialog  :close-on-click-modal="false" 
      v-model="dialogVisible"
      title="预览"
      width="800px"
      append-to-body
    >
      <!-- 条件渲染图片或视频预览 -->
      <img
        v-if="!isVideoUrl(dialogImageUrl)"
        :src="dialogImageUrl"
        style="display: block; max-width: 100%; margin: 0 auto"
      />
      <video
        v-else
        :src="dialogImageUrl"
        controls
        controlsList="nodownload"
        style="display: block; max-width: 100%; margin: 0 auto; max-height: 600px;"
      >
        <source :src="dialogImageUrl" type="video/mp4" />
      </video>
    </el-dialog>
  </div>
</template>

<script setup>
import { getToken } from "@/modules/huyu-framework/backend/utils/auth";
import { ref, computed, watch, getCurrentInstance } from "vue";
import { Delete, ZoomIn, Plus } from "@element-plus/icons-vue";

// 判断是否为视频文件
const isVideoFile = (file) => {
  if (!file) return false;
  const fileName = file.name || (file.url ? file.url.split('/').pop() : '');
  const videoExtensions = ['.mp4', '.avi', '.rmvb', '.mov', '.wmv', '.flv', '.mkv'];
  return videoExtensions.some(ext => fileName.toLowerCase().endsWith(ext));
};

// 判断URL是否为视频URL
const isVideoUrl = (url) => {
  if (!url) return false;
  const videoExtensions = ['.mp4', '.avi', '.rmvb', '.mov', '.wmv', '.flv', '.mkv'];
  return videoExtensions.some(ext => url.toLowerCase().endsWith(ext));
};

// 获取视频首帧图
const getVideoFirstFrame = (videoUrl) => {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.setAttribute('crossOrigin', 'anonymous');
    video.src = videoUrl;
    video.preload = 'metadata';
    
    video.onloadedmetadata = () => {
      // 设置视频播放位置为第一帧
      video.currentTime = 0.1;
    };
    
    video.onseeked = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      // 设置canvas尺寸与视频一致
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      // 绘制第一帧
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      // 转换为base64图片
      const poster = canvas.toDataURL('image/png');
      resolve(poster);
    };
    
    video.onerror = () => {
      resolve('');
    };
  });
};

// 视频首帧图缓存
const videoPosters = ref({});

// 加载视频首帧图
const loadVideoPoster = async (file) => {
  if (isVideoFile(file) && file.url && !videoPosters.value[file.url]) {
    const poster = await getVideoFirstFrame(file.url);
    videoPosters.value[file.url] = poster;
  }
};

const props = defineProps({
  modelValue: [String, Object, Array],
  // 图片数量限制
  limit: {
    type: Number,
    default: 5,
  },
  // 大小限制(MB)
  fileSize: {
    type: Number,
    default: 50,
  },
  // 文件类型, 例如['png', 'jpg', 'jpeg']
  fileType: {
    type: Array,
    default: () => ["mp4", "avi", "rmvb"],
  },
  // 是否显示提示
  isShowTip: {
    type: Boolean,
    default: true,
  },
  // 是否禁用删除
  disabled: {
    type: Boolean,
    default: false,
  },
});

const { proxy } = getCurrentInstance();
const emit = defineEmits();
const number = ref(0);
const uploadList = ref([]);
const dialogImageUrl = ref("");
const dialogVisible = ref(false);
const baseUrl = import.meta.env.VITE_APP_BASE_API;
const uploadImgUrl = ref(import.meta.env.VITE_APP_BASE_API + "/common/upload"); // 上传的图片服务器地址
const headers = ref({ Authorization: "Bearer " + getToken() });
const fileList = ref([]);
const showTip = computed(
  () => props.isShowTip && (props.fileType || props.fileSize)
);

watch(
  () => props.modelValue,
  async (val) => {
    if (val) {
      // 首先将值转为数组
      const list = Array.isArray(val) ? val : props.modelValue.split(",");
      // 然后将数组转为对象数组
      fileList.value = list.map((item) => {
        if (typeof item === "string") {
          if (item.indexOf(baseUrl) === -1) {
            item = { name: baseUrl + item, url: baseUrl + item };
          } else {
            item = { name: item, url: item };
          }
        }
        return item;
      });
      
      // 自动加载所有视频的首帧图
      for (const file of fileList.value) {
        if (isVideoFile(file)) {
          await loadVideoPoster(file);
        }
      }
    } else {
      fileList.value = [];
      return [];
    }
  },
  { deep: true, immediate: true }
);

// 上传前loading加载
function handleBeforeUpload(file) {
  let isImg = false;
  if (props.fileType.length) {
    let fileExtension = "";
    if (file.name.lastIndexOf(".") > -1) {
      fileExtension = file.name.slice(file.name.lastIndexOf(".") + 1);
    }
    isImg = props.fileType.some((type) => {
      if (file.type.indexOf(type) > -1) return true;
      if (fileExtension && fileExtension.indexOf(type) > -1) return true;
      return false;
    });
  } else {
    isImg = file.type.indexOf("image") > -1;
  }
  if (!isImg) {
      proxy.$modal.msgError(
        `文件格式不正确, 请上传${props.fileType.join("/")}视频格式文件!`
      );
      return false;
    }
  if (props.fileSize) {
    const isLt = file.size / 1024 / 1024 < props.fileSize;
    if (!isLt) {
      proxy.$modal.msgError(`上传头像图片大小不能超过 ${props.fileSize} MB!`);
      return false;
    }
  }
  proxy.$modal.loading("正在上传图片，请稍候...");
  number.value++;
}

// 文件个数超出
function handleExceed() {
  proxy.$modal.msgError(`上传文件数量不能超过 ${props.limit} 个!`);
}

// 上传成功回调
function handleUploadSuccess(res, file) {
  if (res.code === 200) {
    uploadList.value.push({ name: res.fileName, url: res.fileName });
    uploadedSuccessfully();
  } else {
    number.value--;
    proxy.$modal.closeLoading();
    proxy.$modal.msgError(res.msg);
    proxy.$refs.imageUpload.handleRemove(file);
    uploadedSuccessfully();
  }
}

// 删除图片
function handleDelete(file) {
  const findex = fileList.value.map((f) => f.name).indexOf(file.name);
  if (findex > -1 && uploadList.value.length === number.value) {
    fileList.value.splice(findex, 1);
    emit("update:modelValue", listToString(fileList.value));
    return false;
  }
}

// 上传结束处理
function uploadedSuccessfully() {
  if (number.value > 0 && uploadList.value.length === number.value) {
    fileList.value = fileList.value
      .filter((f) => f.url !== undefined)
      .concat(uploadList.value);
    uploadList.value = [];
    number.value = 0;
    emit("update:modelValue", listToString(fileList.value));
    proxy.$modal.closeLoading();
  }
}

// 上传失败
function handleUploadError() {
  proxy.$modal.msgError("上传图片失败");
  proxy.$modal.closeLoading();
}

// 预览
function handlePictureCardPreview(file) {
  dialogImageUrl.value = file.url;
  dialogVisible.value = true;
}

// 对象转成指定字符串分隔
function listToString(list, separator) {
  let strs = "";
  separator = separator || ",";
  for (let i in list) {
    if (undefined !== list[i].url && list[i].url.indexOf("blob:") !== 0) {
      strs += list[i].url.replace(baseUrl, "") + separator;
    }
  }
  return strs != "" ? strs.substr(0, strs.length - 1) : "";
}
</script>

<style scoped lang="scss">
// .el-upload--picture-card 控制加号部分
:deep(.hide .el-upload--picture-card) {
  display: none;
}

// 视频文件项样式
.video-file-item {
  position: relative;
  width: 100%;
  height: 100%;
}

// 视频首帧图样式
.video-poster {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  z-index: 1;
}

// 视频预览样式
.video-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

// 视频播放按钮覆盖层
.video-file-item::after {

}

// 播放按钮三角图标
.video-file-item::before {

}

// 上传列表项样式调整
:deep(.el-upload-list__item) {
  position: relative;
  height: 148px;
}

// 上传列表项动作按钮样式调整
:deep(.el-upload-list__item-actions) {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  background: rgba(0, 0, 0, 0.5);
  opacity: 0;
  transition: opacity 0.3s;
  text-align: center;
  padding: 10px 0;
}

:deep(.el-upload-list__item:hover .el-upload-list__item-actions) {
  opacity: 1;
}

:deep(.el-upload-list__item-actions span) {
  color: white;
  margin: 0 10px;
  cursor: pointer;
}
</style>
